import { useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import { Reveal } from "./Reveal";
import { REVIEWS } from "../data/content";

const ReviewCard = ({ review }) => (
  <figure
    data-testid={`review-item-card-${review.id}`}
    className="flex h-full flex-col rounded-[1.75rem] border border-[#E4DBD0] bg-white p-7 transition-shadow duration-500 hover:shadow-[0_30px_60px_-30px_rgba(32,27,23,0.3)] md:p-8"
  >
    <div className="flex items-center gap-1" aria-label={`${review.rating} out of 5 stars`}>
      {Array.from({ length: review.rating }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-[#C4623C] text-[#C4623C]" />
      ))}
    </div>
    <blockquote className="font-serif-display mt-5 flex-1 text-xl leading-snug text-[#201B17] md:text-[1.35rem]">
      “{review.text}”
    </blockquote>
    <figcaption className="mt-7 border-t border-[#F0EAE0] pt-5">
      <p className="text-sm font-medium text-[#201B17]">{review.author}</p>
      <p className="font-mono-accent mt-1.5 text-[10px] uppercase tracking-[0.16em] text-[#8C7F72]">
        {review.location} · {review.stayDate} · {review.property}
      </p>
    </figcaption>
  </figure>
);

export const Reviews = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-14 flex items-end justify-between gap-6">
          <Reveal>
            <p className="font-mono-accent text-[11px] uppercase tracking-[0.28em] text-[#C4623C]">
              Guest book
            </p>
            <h2 className="font-serif-display mt-5 max-w-xl text-4xl leading-[1.05] tracking-tight text-[#201B17] sm:text-5xl lg:text-6xl">
              Words from <em className="text-[#C4623C]">our guests.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="hidden sm:block">
            <div className="flex gap-3">
              <button
                onClick={scrollPrev}
                data-testid="review-carousel-prev-btn"
                aria-label="Previous reviews"
                className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E4DBD0] text-[#201B17] transition-colors duration-300 hover:bg-[#201B17] hover:text-[#FBF8F3]"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                onClick={scrollNext}
                data-testid="review-carousel-next-btn"
                aria-label="Next reviews"
                className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E4DBD0] text-[#201B17] transition-colors duration-300 hover:bg-[#201B17] hover:text-[#FBF8F3]"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="embla" ref={emblaRef} data-testid="reviews-carousel">
            <div className="embla__container">
              {REVIEWS.map((review) => (
                <div
                  key={review.id}
                  className="min-w-0 flex-[0_0_100%] pr-5 md:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
                >
                  <ReviewCard review={review} />
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-8 flex justify-center gap-3 sm:hidden">
          <button
            onClick={scrollPrev}
            data-testid="review-carousel-prev-btn-mobile"
            aria-label="Previous reviews"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E4DBD0] text-[#201B17]"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            onClick={scrollNext}
            data-testid="review-carousel-next-btn-mobile"
            aria-label="Next reviews"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E4DBD0] text-[#201B17]"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};
